// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { useTheme } from '@mui/material';

import HtmlMapMarker from './HtmlMapMarker';
import PinMarker from './PinMarker';
import {
  getMarkerBackgroundColor,
  getMarkerForegroundColor,
  getMarkerIcon,
  type LocationPinVariant,
} from './markerTheme';

// TYPES ---------------------------------------------------------------- //

export type { LocationPinVariant };

export type LocationPinBubbleTrigger = 'hover' | 'selected' | 'always';

/** Read by `LocationPinBubble` to drive its fade-in/out transition. */
export const LocationPinBubbleVisibilityContext = React.createContext<boolean>(true);

interface LocationPinMarkerProps {
  mapId?: string;
  markerId: LocationPinVariant;
  position: { lat: number; lng: number };
  size?: number;
  iconSize?: number;
  hintText?: string;
  hoverBrighter?: boolean;
  /** Stacking order against other `LocationPinMarker`s on the same map. Default `1`. */
  zIndex?: number;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** When to show the bubble passed as children. Default: `'hover'`. */
  bubbleTrigger?: LocationPinBubbleTrigger;
  /** External open-state used when `bubbleTrigger === 'selected'`. */
  bubbleOpen?: boolean;
  /** Optional heading rotation in degrees applied to the icon only (not the
   * pin chrome or bubble). Use for directional markers like a moving driver
   * where the icon should indicate travel direction. */
  rotation?: number;
  children?: React.ReactNode;
}

// COMPONENT ---------------------------------------------------------------- //

/**
 * Map marker for a route endpoint or location. Picks theme color + icon from
 * `markerId`: `pickup` → info + play, `delivery` → success + flag, `driver` →
 * primary + user, `favorite` → utility + heart, anything else → utility + pin.
 * Pass `children` to render a bubble above the pin; `bubbleTrigger` controls
 * when that bubble is visible.
 */
const LocationPinMarker = ({
  mapId,
  markerId,
  position,
  size = 36,
  iconSize = 20,
  hintText,
  hoverBrighter,
  zIndex,
  onClick,
  bubbleTrigger = 'hover',
  bubbleOpen,
  rotation,
  children,
}: LocationPinMarkerProps) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = React.useState(false);

  const IconComponent = getMarkerIcon(markerId);
  const backgroundColor = getMarkerBackgroundColor(theme, markerId);
  const foregroundColor = getMarkerForegroundColor(theme, markerId);

  const shouldShowBubble =
    bubbleTrigger === 'always'
      ? true
      : bubbleTrigger === 'selected'
        ? !!bubbleOpen
        : isHovered;

  // Lift the whole marker above sibling pins while its bubble is showing so
  // the bubble never renders underneath another pin in the same overlay pane.
  const effectiveZIndex = shouldShowBubble ? Math.max(zIndex ?? 1, 999) : zIndex;

  return (
    <HtmlMapMarker mapId={mapId ?? ''} position={position} zIndex={effectiveZIndex}>
      <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <PinMarker
          backgroundColor={backgroundColor}
          foregroundColor={foregroundColor}
          size={size}
          hintText={hintText}
          hoverBrighter={hoverBrighter}
          onClick={onClick}
          icon={
            <div
              style={{
                display: 'inline-flex',
                transform: rotation != null ? `rotate(${rotation}deg)` : undefined,
                transformOrigin: 'center',
              }}
            >
              <IconComponent size={iconSize} style={{ color: backgroundColor }} />
            </div>
          }
        >
          <LocationPinBubbleVisibilityContext.Provider value={shouldShowBubble}>
            {children}
          </LocationPinBubbleVisibilityContext.Provider>
        </PinMarker>
      </div>
    </HtmlMapMarker>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default LocationPinMarker;
